import { readAssetDetails } from "./asset-attribution";
import { artworkFields } from "../react-components/contexts/UploadFormContext";

/**
 * Maps asset API response data to form field values
 * Handles three asset categories: "file", "digital-art", "digital-twin-art"
 * @param {Object} asset - Asset object from API response
 * @returns {Object} Object with artworkFields keys mapped to asset values
 */
export function mapAssetToFormFields(asset) {
  if (!asset) return {};

  const category = asset.asset_category;
  const details = readAssetDetails(asset);
  
  // Extract title and description - they should be in sync across asset and artwork
  // Priority: artwork.title > asset.title for artworks, asset.title for files
  const artwork = asset.metadata?.artwork || {};
  const artworkMetadata = artwork.metadata || {};
  
  // Unified title/name extraction
  const titleValue = (category === "digital-art" || category === "digital-twin-art") 
    ? (artwork.title || asset.title || "")
    : (asset.title || "");
  
  // Unified description extraction  
  const descriptionValue = (category === "digital-art" || category === "digital-twin-art")
    ? (artwork.description || asset.description || "")
    : (asset.description || "");

  // For basic "file" category assets
  if (category === "file" || !category) {
    return {
      ...details,
      [artworkFields.myMediaId]: asset.id,
      [artworkFields.title]: titleValue,
      [artworkFields.description]: descriptionValue,
      [artworkFields.url]: asset.url || "",
      // Store additional reference data
      uuid: asset.uuid,
      type: asset.type,
      thumbnail: asset.thumbnail
    };
  }

  // For "digital-art" and "digital-twin-art" categories
  if (category === "digital-art" || category === "digital-twin-art") {
    const mappedFields = {
      // Basic identification
      ...details,
      [artworkFields.myMediaId]: asset.id,
      uuid: asset.uuid,
      type: asset.type,
      thumbnail: asset.thumbnail,

      // Basic artwork info - using unified extraction
      [artworkFields.artName]: titleValue,
      [artworkFields.description]: descriptionValue,
      [artworkFields.artworkDate]: artwork.artworkDate || "",

      // Work information
      // Handle medium - can be string or object
      [artworkFields.medium]: (() => {
        const medium = artwork.medium || artworkMetadata.medium;
        if (!medium) return "";
        if (typeof medium === "string") return medium;
        return medium.name || medium.label || "";
      })(),
      // Handle frame - can be string or object
      [artworkFields.frame]: (() => {
        const frame = artworkMetadata.frame || artwork.frame;
        if (!frame) return "";
        if (typeof frame === "string") return frame;
        return frame.name || frame.label || "";
      })(),
      [artworkFields.dimensionsX]: artwork.dimensionsX || "",
      [artworkFields.dimensionsY]: artwork.dimensionsY || "",
      [artworkFields.dimensionsZ]: artwork.dimensionsZ || "",
      [artworkFields.dimensions]: artwork.dimensions || "",

      // Artist information
      [artworkFields.artistName]: artwork.author || "",
      [artworkFields.artistBio]: artworkMetadata.artistBio || "",
      [artworkFields.artistStatement]: artwork.artistStatement || "",
      [artworkFields.authorDate]: artwork.authorDate || artworkMetadata.authorDate || "",

      // Legal and rights information
      [artworkFields.declareRights]: artworkMetadata.declareRights || false,
      [artworkFields.terms]: artworkMetadata.terms || false,

      // AI creation information
      [artworkFields.AIcreation]: artworkMetadata.workCreatedByAI || artworkMetadata[artworkFields.AIcreation] || false,
      [artworkFields.aiToolUsed]: artworkMetadata.aiToolUsed || "",

      // Technical fields
      [artworkFields.techniqueUsed]: artworkMetadata.techniqueUsed || "",
      [artworkFields.currentPhysicalWork]: artworkMetadata.currentPhysicalWork || "",
      [artworkFields.customMedium]: artworkMetadata.customMedium || "",

      // Additional metadata
      [artworkFields.enableARTrigger]: artworkMetadata.enableARTrigger || false,
      [artworkFields.isPublicDomainArt]: artworkMetadata.isPublicDomainArt || false,
      labels: artworkMetadata.labels || ""
    };

    return mappedFields;
  }

  // Fallback for unknown categories - use unified extraction
  return {
    ...details,
    [artworkFields.myMediaId]: asset.id,
    [artworkFields.title]: titleValue,
    [artworkFields.description]: descriptionValue,
    uuid: asset.uuid,
    type: asset.type,
    thumbnail: asset.thumbnail
  };
}
